/*
 * qB search dropdown for Moana Road (Prestige 5.0.3), Oct 2026.
 *
 * Takes over the "as you type" part of the theme's search drawer (#Search in sections/header.liquid).
 * The theme's own SearchBar (theme.min.js) still opens, closes and traps focus; this file only stops its
 * input/keydown handlers from running, by listening in the capture phase on #Search.
 *
 * Two requests per search, in parallel, both rendered by sections/predictive-search.liquid:
 *   - products: the same full search as the results page (no "*" wildcard), so boosts apply and counts match
 *   - suggestions: Shopify predictive search (suggested searches, collections, articles)
 * Delete the <script> tag in header.liquid to fall back to the theme's original dropdown.
 */
(function () {
  'use strict';

  var root = document.getElementById('Search');
  if (!root || !window.fetch || !window.Promise || !('content' in document.createElement('template'))) return;

  var input = root.querySelector('input[name="q"]');
  var results = root.querySelector('.Search__Results');
  var status = root.querySelector('[data-qb-search-status]');
  var emptyTemplate = document.getElementById('QbSearchEmpty');
  if (!input || !results) return;

  var searchUrl = root.getAttribute('data-search-url') || '/search';
  var suggestUrl = root.getAttribute('data-suggest-url') || '/search/suggest';
  var searchMode = (window.theme && window.theme.searchMode) || 'product';
  var suggestTypes = ['query', 'collection'];
  if (searchMode.indexOf('article') !== -1) suggestTypes.push('article');
  if (searchMode.indexOf('page') !== -1) suggestTypes.push('page');

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 200;
  var timer = null;
  var requestId = 0;

  root.classList.add('qb-search--ready');

  function setResults(html) {
    results.innerHTML = html;
    results.setAttribute('aria-hidden', html ? 'false' : 'true');
  }

  function announce(text) {
    if (status) status.textContent = text || '';
  }

  function emptyStateHtml() {
    return emptyTemplate ? emptyTemplate.innerHTML : '';
  }

  // Empty box: popular searches and collections, plus best sellers on the right (fetched once, then reused).
  var bestSellersUrl = root.getAttribute('data-best-sellers-url');
  var bestSellers = null; // the .qb-search__products--best element once loaded
  var bestSellersRequested = false;

  function renderEmptyState() {
    if (!bestSellers) {
      setResults(emptyStateHtml());
      return;
    }
    var layout = document.createElement('div');
    layout.className = 'qb-search__layout';
    var aside = document.createElement('div');
    aside.className = 'qb-search__aside';
    aside.innerHTML = emptyStateHtml();
    var main = document.createElement('div');
    main.className = 'qb-search__main';
    main.appendChild(bestSellers.cloneNode(true));
    layout.appendChild(aside);
    layout.appendChild(main);
    results.innerHTML = '';
    results.appendChild(layout);
    results.setAttribute('aria-hidden', 'false');
  }

  function showEmptyState() {
    requestId++;
    renderEmptyState();
    announce('');
    if (bestSellersUrl && !bestSellersRequested) {
      bestSellersRequested = true;
      fetchText(bestSellersUrl + '?section_id=predictive-search').then(function (html) {
        var block = sectionContent(html, '.qb-search__products--best');
        if (!block || !block.querySelector('.qb-search__product')) return;
        bestSellers = block;
        // Only redraw if the box is still empty (the shopper may have started typing meanwhile)
        if (input.value.trim().length < MIN_CHARS) renderEmptyState();
      });
    }
  }

  // Section Rendering API responses come wrapped in <div id="shopify-section-...">; return the inner element.
  function sectionContent(html, selector) {
    if (!html) return null;
    var holder = document.createElement('div');
    holder.innerHTML = html;
    return holder.querySelector(selector);
  }

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.text() : ''; })
      .catch(function () { return ''; });
  }

  function search(query) {
    var id = ++requestId;
    var q = encodeURIComponent(query);
    var productsUrl = searchUrl + '?q=' + q + '&type=product&section_id=predictive-search';
    var suggestionsUrl = suggestUrl + '?q=' + q +
      '&resources[type]=' + suggestTypes.join(',') +
      '&resources[limit]=4&resources[limit_scope]=each&section_id=predictive-search';

    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    Promise.all([fetchText(productsUrl), fetchText(suggestionsUrl)]).then(function (responses) {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      if (id !== requestId) return; // a newer search has started

      var products = sectionContent(responses[0], '.qb-search__products');
      var suggestions = sectionContent(responses[1], '.qb-search__suggest');
      var productCount = products ? parseInt(products.getAttribute('data-qb-count'), 10) || 0 : 0;
      var suggestionCount = suggestions ? parseInt(suggestions.getAttribute('data-qb-suggest-count'), 10) || 0 : 0;

      if (!products && !suggestions) {
        // Both requests failed: leave the old results in place rather than blanking the dropdown.
        return;
      }

      var layout = document.createElement('div');
      layout.className = 'qb-search__layout' + (suggestionCount > 0 ? '' : ' qb-search__layout--no-aside');

      if (suggestionCount > 0) {
        var aside = document.createElement('div');
        aside.className = 'qb-search__aside';
        aside.appendChild(suggestions);
        layout.appendChild(aside);
      }

      var main = document.createElement('div');
      main.className = 'qb-search__main';
      if (products) {
        // Liquid only has the HTML-escaped search.terms, so rebuild the "View all" links from what was typed.
        var viewAllUrl = searchUrl + '?q=' + q + '&type=product';
        Array.prototype.forEach.call(products.querySelectorAll('.qb-search__view-all, .qb-search__view-all-link'), function (link) {
          link.setAttribute('href', viewAllUrl);
        });
        main.appendChild(products);
      }
      if (productCount === 0 && suggestionCount === 0) main.insertAdjacentHTML('beforeend', emptyStateHtml());
      layout.appendChild(main);

      results.innerHTML = '';
      results.appendChild(layout);
      results.setAttribute('aria-hidden', 'false');
      announce(products ? products.getAttribute('data-qb-status') : '');
    });
  }

  // Capture phase on #Search runs before the input's own listeners (theme.min.js SearchBar) and stops them.
  root.addEventListener('input', function (event) {
    if (event.target !== input) return;
    event.stopPropagation();

    clearTimeout(timer);
    var query = input.value.trim();
    if (query.length < MIN_CHARS) {
      showEmptyState();
      return;
    }
    timer = setTimeout(function () { search(query); }, DEBOUNCE_MS);
  }, true);

  root.addEventListener('keydown', function (event) {
    if (event.target !== input) return;

    if (event.key === 'Enter' || event.keyCode === 13) {
      // The theme blocks Enter when articles are searchable; we let it submit to the results page instead.
      event.stopPropagation();
      if (!input.value.trim()) event.preventDefault();
    } else if (event.key === 'Escape' || event.keyCode === 27) {
      if (root.getAttribute('aria-hidden') === 'false') {
        document.body.dispatchEvent(new CustomEvent('search:close', { bubbles: true }));
      }
    }
  }, true);

  // When the drawer opens with an empty box, show popular searches and collections straight away.
  new MutationObserver(function () {
    if (root.getAttribute('aria-hidden') === 'false' && !input.value.trim()) showEmptyState();
  }).observe(root, { attributes: true, attributeFilter: ['aria-hidden'] });

  /* ---------- Results page (sections/main-search.liquid) ---------- */

  // Empty or no-results page: reuse the dropdown's popular searches / collections.
  Array.prototype.forEach.call(document.querySelectorAll('[data-qb-search-popular]'), function (slot) {
    slot.innerHTML = emptyStateHtml();
  });

  // No-results page: offer Shopify's suggested searches ("Did you mean").
  var didYouMean = document.querySelector('[data-qb-did-you-mean]');
  if (didYouMean) {
    var term = (didYouMean.getAttribute('data-q') || '').trim();
    var linksSlot = didYouMean.querySelector('[data-qb-did-you-mean-links]');
    if (term && linksSlot) {
      fetchText(suggestUrl + '?q=' + encodeURIComponent(term) +
        '&resources[type]=query&resources[limit]=3&section_id=predictive-search').then(function (html) {
        var suggestions = sectionContent(html, '.qb-search__suggest');
        var links = suggestions ? suggestions.querySelectorAll('.qb-search__link--query') : [];
        var shown = 0;
        Array.prototype.forEach.call(links, function (link) {
          if (link.textContent.trim().toLowerCase() === term.toLowerCase()) return;
          link.className = 'qb-search-page__dym-link Link Link--underline';
          linksSlot.appendChild(link);
          shown++;
        });
        if (shown > 0) didYouMean.hidden = false;
      });
    }
  }

  /* ---------- Product page: "Pairs well with" (sections/product-recommendations.liquid) ---------- */
  // Complementary products come from Search & Discovery; the slot stays hidden until some are set.
  // A product shown in "Pairs well with" is removed from "You may also like" below it, so nothing shows twice.
  function productHandle(href) {
    var m = (href || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function removePairedFromRelated(slot) {
    var section = slot.closest('[data-section-type="product-recommendations"]');
    var related = section && section.querySelector('.ProductRecommendations');
    if (!related) return;
    var paired = {};
    Array.prototype.forEach.call(slot.querySelectorAll('a[href*="/products/"]'), function (a) {
      var h = productHandle(a.getAttribute('href'));
      if (h) paired[h] = true;
    });
    var carousel = related.querySelector('[data-flickity-config]');
    var flickity = window.Flickity && carousel && window.Flickity.data ? window.Flickity.data(carousel) : null;
    // On phones the theme's carousel (watchCSS) exists but is dormant: only use Flickity's API when it is active.
    var active = !!(flickity && flickity.isActive);
    var removed = 0;
    Array.prototype.forEach.call(related.querySelectorAll('.Carousel__Cell'), function (cell) {
      var link = cell.querySelector('a[href*="/products/"]');
      if (!link || !paired[productHandle(link.getAttribute('href'))]) return;
      var done = false;
      if (active) {
        try { flickity.remove(cell); done = true; } catch (e) { /* fall back to a plain DOM removal */ }
      }
      if (!done && cell.parentNode) cell.parentNode.removeChild(cell);
      removed++;
    });
    if (!removed) return;
    // Only when de-duplicating emptied it (not while the theme is still loading it): hide "You may also like".
    if (!related.querySelector('.Carousel__Cell')) {
      related.hidden = true;
      // The section's own heading, not the "Pairs well with" one inside the slot
      var header = section.querySelector(':scope > .SectionHeader');
      if (header) header.hidden = true;
    } else if (active) {
      try { flickity.resize(); } catch (e) { /* layout recovers on the next resize */ }
    }
  }

  Array.prototype.forEach.call(document.querySelectorAll('[data-qb-pairs-url]'), function (slot) {
    fetchText(slot.getAttribute('data-qb-pairs-url')).then(function (html) {
      var content = sectionContent(html, '[data-qb-pairs-content]');
      if (!content || !content.querySelector('.ProductItem')) return;
      slot.innerHTML = '';
      slot.appendChild(content);
      slot.hidden = false;
      // "You may also like" is loaded and rebuilt by the theme after page load; de-duplicate again whenever it changes.
      // The observer is attached first so a failure in the first pass can never skip it.
      var related = slot.closest('[data-section-type="product-recommendations"]');
      related = related && related.querySelector('.ProductRecommendations');
      if (related) {
        var pending = false;
        new MutationObserver(function () {
          if (pending) return;
          pending = true;
          setTimeout(function () {
            pending = false;
            try { removePairedFromRelated(slot); } catch (e) { /* never break the page */ }
          }, 50);
        }).observe(related, { childList: true, subtree: true });
      }
      try { removePairedFromRelated(slot); } catch (e) { /* never break the page */ }
    });
  });
})();
